import React, { useEffect, useState } from 'react';
import {
  useSearchParams } from 'react-router-dom';
import {
  Box,
  Center,
  Flex,
  Heading,
  Image,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { publicMetricasAPI } from '../services/api';
import { metricasEs } from '../locales/metricas.es.js';
import { MetricasResumenPublicContext } from '../context/MetricasResumenPublicContext.jsx';
import { MetricasInformeHero } from './MetricasInformeContenido.jsx';
import MetricasSpotifyResumenCuerpo from './MetricasSpotifyResumenCuerpo.jsx';

const t = metricasEs;

/** Logo para fondo oscuro (public/ y dist/) */
const LOGO_CADENCYA_BLANCO = '/logo y texto cadencya blanco.png';

const MotionBox = motion.create(Box);
const WRAPPED = {
  sub: 'gray.400',
  informeHeroBg:
    'linear-gradient(135deg, rgba(88, 28, 135, 0.28) 0%, rgba(23, 25, 35, 0.96) 55%, rgba(6, 78, 78, 0.14) 100%)',
  informeHeroBorder: 'whiteAlpha.200',
  pageBg: '#070510',
  headerBg: 'rgba(0,0,0,0.45)',
  accentOrb: 'rgba(124, 58, 237, 0.35)',
};

function MetricasWrappedIntro({ data, modoInforme, tMetricas }) {
  const subKicker =
    modoInforme.tipo === 'anio' ? tMetricas.wrapped.subYear : tMetricas.wrapped.subRange;
  return (
    <Flex
      as="section"
      direction="column"
      flex="1"
      minH={0}
      w="100%"
      aria-label="Inicio del resumen"
    >
      <VStack
        flex="1"
        minH={0}
        w="100%"
        justify="center"
        textAlign="center"
        gap={{ base: 3, md: 4 }}
        py={{ base: 2, md: 3 }}
        px={1}
      >
        {data.bandaNombre || data.bandaAvatarUrl ? (
          <Flex
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            justify="center"
            align="center"
            direction={{ base: 'column', sm: 'row' }}
            gap={{ base: 4, md: 5 }}
            px={2}
            w="100%"
            asChild
          ><motion.div>
              {data.bandaAvatarUrl ? (
                <Image
                  src={data.bandaAvatarUrl}
                  alt={data.bandaNombre || ''}
                  boxSize={{ base: '100px', sm: '120px', md: '140px' }}
                  minW={{ base: '100px', sm: '120px', md: '140px' }}
                  borderRadius="full"
                  objectFit="cover"
                  borderWidth="3px"
                  borderColor="purple.400"
                  boxShadow="0 0 0 1px rgba(255,255,255,0.12), 0 16px 48px rgba(0,0,0,0.5)"
                  flexShrink={0}
                />
              ) : null}
              {data.bandaNombre ? (
                <Text
                  fontSize={{ base: 'xl', sm: '2xl', md: '2xl' }}
                  fontWeight="800"
                  color="whiteAlpha.900"
                  letterSpacing="0.12em"
                  textTransform="uppercase"
                  textAlign={{ base: 'center', sm: 'left' }}
                  lineHeight="1.2"
                  maxW="lg"
                >
                  {data.bandaNombre}
                </Text>
              ) : null}
            </motion.div></Flex>
        ) : null}
        <Text
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          fontSize="sm"
          color="whiteAlpha.700"
          fontWeight="600"
          asChild
        ><motion.p>
            {tMetricas.wrapped.titleGeneric}
          </motion.p></Text>
        <Heading
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06, type: 'spring', damping: 22 }}
          size="2xl"
          fontWeight="900"
          letterSpacing="-0.03em"
          lineHeight="1.1"
          bgGradient="linear(to-r, #faf5ff, #c4b5fd, #5eead4)"
          bgClip="text"
          color="transparent"
          px={2}
          asChild
        ><motion.h1>
            {tMetricas.wrapped.sectionNumbers}
          </motion.h1></Heading>
        <Text
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.28 }}
          color="whiteAlpha.500"
          fontSize="sm"
          asChild
        ><motion.p>
            {subKicker}
          </motion.p></Text>
      </VStack>

      <VStack
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.45 }}
        flexShrink={0}
        w="100%"
        gap={1}
        pt={2}
        pb={{ base: 'max(1.25rem, env(safe-area-inset-bottom))', md: 'max(2rem, env(safe-area-inset-bottom))' }}
        borderTopWidth="1px"
        borderColor="whiteAlpha.100"
        bg="linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.2) 100%)"
        asChild
      ><motion.div>
          <Text
            color="whiteAlpha.800"
            fontSize={{ base: 'md', sm: 'lg', md: 'xl' }}
            fontWeight="800"
            letterSpacing="0.2em"
            textTransform="uppercase"
            lineHeight="1.2"
          >
            {tMetricas.wrapped.keepScrolling}
          </Text>
          <MotionBox
            color="purple.300"
            fontSize={{ base: '2.5rem', md: '3.5rem' }}
            lineHeight="1"
            aria-hidden
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 10 }}
            transition={{ delay: 0.65, duration: 1.2, repeat: Infinity, repeatType: 'reverse' }}
          >
            ↓
          </MotionBox>
        </motion.div></VStack>
    </Flex>
  );
}

/**
 * Página pública: cada visita recalcula vía API (JWT en `t=`), sin guardar en BBDD.
 * Mismo informe y orden que en Estadísticas (vista "wrapped" oscura y animada).
 */
export default function MetricasResumenPublic() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [payload, setPayload] = useState(null);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setErr(t.wrapped.noToken);
      return;
    }
    let alive = true;
    (async () => {
      setErr(null);
      setLoading(true);
      try {
        const { data } = await publicMetricasAPI.getResumen(token);
        if (alive) setPayload(data);
      } catch (e) {
        if (alive) {
          setErr(e?.response?.data?.message || t.wrapped.loadError);
          setPayload(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) {
    return (
      <Center minH="100vh" bg="gray.900" position="relative" overflow="hidden">
        <Box
          position="absolute"
          inset={0}
          bgGradient="radial(circle at 50% 0%, purple.800 0%, transparent 55%)"
          opacity={0.35}
        />
        <VStack gap={4} position="relative" zIndex={1}>
          <Spinner size="xl" color="purple.300" borderWidth="3px" />
          <Text color="whiteAlpha.800">Cadencya</Text>
        </VStack>
      </Center>
    );
  }

  if (err && !payload) {
    return (
      <Center minH="100vh" bg={WRAPPED.pageBg} px={4}>
        <VStack gap={3} maxW="md" textAlign="center">
          <Image src={LOGO_CADENCYA_BLANCO} alt="Cadencya" maxH="64px" w="auto" objectFit="contain" />
          <Text color="whiteAlpha.900" fontWeight="600">
            {err}
          </Text>
        </VStack>
      </Center>
    );
  }

  const data = payload;
  const hasResumen = data?.resumen && data?.ok && !data?.vacio;
  const modoInforme = data?.modoInforme ?? { tipo: 'rango' };

  if (!hasResumen) {
    return (
      <Center minH="100vh" bg={WRAPPED.pageBg} px={4}>
        <VStack gap={4} maxW="lg" textAlign="center">
          <Image src={LOGO_CADENCYA_BLANCO} alt="Cadencya" maxH="64px" w="auto" objectFit="contain" />
          <Text color="whiteAlpha.800">
            {t.wrapped.noData}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t.wrapped.pageTitle}</title>
      </Helmet>
      <MetricasResumenPublicContext.Provider value>
      <Box
        as="main"
        minH="100vh"
        position="relative"
        overflow="auto"
        bg={WRAPPED.pageBg}
        color="gray.100"
        pb={16}
        css={{
          '& scrollSnapType': { base: 'none', md: 'y proximity' },
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          zIndex={0}
          bg="linear-gradient(180deg, rgba(22, 11, 40, 0.95) 0%, #070510 45%, #050308 100%)"
        />
        <MotionBox
          position="absolute"
          w="min(80vw, 480px)"
          h="min(80vw, 480px)"
          top="-8%"
          left="50%"
          marginLeft="min(-40vw, -240px)"
          borderRadius="full"
          bg={WRAPPED.accentOrb}
          filter="blur(80px)"
          opacity={0.9}
          zIndex={0}
          animate={{ scale: [1, 1.06, 1], opacity: [0.75, 0.95, 0.75] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <MotionBox
          position="absolute"
          w="min(60vw, 320px)"
          h="min(60vw, 320px)"
          bottom="10%"
          right="-5%"
          borderRadius="full"
          bg="rgba(6, 182, 212, 0.12)"
          filter="blur(64px)"
          zIndex={0}
          animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.65, 0.4] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />

        <Box
          maxW="6xl"
          mx="auto"
          w="100%"
          position="relative"
          zIndex={1}
          minH="100dvh"
          display="flex"
          flexDirection="column"
        >
          <Flex
            position="sticky"
            top={0}
            zIndex={20}
            px={{ base: 3, md: 6 }}
            py={3}
            bg={WRAPPED.headerBg}
            backdropFilter="blur(10px)"
            borderBottomWidth="1px"
            borderColor="whiteAlpha.200"
            align="center"
            justify="flex-start"
            flexShrink={0}
          >
            <Image
              src={LOGO_CADENCYA_BLANCO}
              alt="Cadencya"
              h={{ base: '40px', md: '52px' }}
              w="auto"
              objectFit="contain"
            />
          </Flex>

          <Box
            flex="1"
            display="flex"
            flexDirection="column"
            minH={0}
            px={{ base: 3, md: 6 }}
            pt={2}
          >
            <MetricasWrappedIntro data={data} modoInforme={modoInforme} tMetricas={t} />
          </Box>
        </Box>

        <Box maxW="6xl" mx="auto" px={{ base: 3, md: 6 }} position="relative" zIndex={1}>
          <VStack align="stretch" gap={6} mt={2}>
            <MetricasInformeHero
              data={data}
              modoInforme={modoInforme}
              tMetricas={t}
              sub={WRAPPED.sub}
              informeHeroBorder={WRAPPED.informeHeroBorder}
              informeHeroBg={WRAPPED.informeHeroBg}
            />
            <MetricasSpotifyResumenCuerpo
              data={data}
              tMetricas={t}
              sub={WRAPPED.sub}
              vistaGraficos={false}
              incluirNarrativa={Boolean(data.incluirNarrativa)}
            />
          </VStack>

          <VStack gap={2} mt={12} textAlign="center">
            <Text initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} fontSize="md" color="whiteAlpha.700" fontWeight="500" asChild><motion.p>
                {t.wrapped.disfruta}
              </motion.p></Text>
            <Flex justify="center" align="center" gap={2} flexWrap="wrap">
              <Text fontSize="sm" color="whiteAlpha.500">
                {t.wrapped.footer}
              </Text>
              <Image
                src={LOGO_CADENCYA_BLANCO}
                alt="Cadencya"
                h={{ base: '28px', sm: '32px' }}
                w="auto"
                objectFit="contain"
              />
            </Flex>
          </VStack>
        </Box>
      </Box>
      </MetricasResumenPublicContext.Provider>
    </>
  );
}
